import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Tag, CircleNotch, Sparkle, ChatCircleText, Hash, ChartBar } from "@phosphor-icons/react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell } from "recharts";

const PALETTE = ["#0033FF", "#7C3AED", "#0EA5E9", "#10B981", "#F59E0B", "#EC4899", "#14B8A6", "#F97316"];

export default function Topics() {
  const [data, setData] = useState(null);
  const [running, setRunning] = useState(false);

  const load = useCallback(() => {
    api.get("/analytics/topics").then((r) => setData(r.data)).catch(() => setData(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const runAnalysis = async () => {
    setRunning(true);
    try {
      const { data: res } = await api.post("/analyze/run");
      if (res.analyzed > 0) toast.success(`${res.analyzed} konversationer analyserade${res.remaining ? ` · ${res.remaining} kvar` : ""}`);
      else toast.info("Inga nya konversationer att analysera.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunde inte köra analysen");
    } finally { setRunning(false); }
  };

  if (data === null) return <div className="p-20 flex justify-center"><CircleNotch size={28} className="animate-spin text-[#0033FF]" /></div>;
  if (data === false) return <div className="p-8 text-zinc-500">Kunde inte ladda ämnesdata.</div>;

  const empty = (data.total || 0) === 0;
  const chartData = (data.topics || []).slice(0, 10).map((t) => ({ name: t.topic, value: t.count }));
  const maxTag = Math.max(1, ...(data.tags || []).map((t) => t.count));

  return (
    <div className="p-6 sm:p-8" data-testid="topics-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-[#0033FF]/10 flex items-center justify-center"><Tag size={22} weight="bold" className="text-[#0033FF]" /></div>
          <div>
            <h1 className="font-display font-bold text-3xl tracking-tight">Ämnen & taggar</h1>
            <p className="text-zinc-500">AI klustrar vad kunderna faktiskt frågar om — se topp-ämnen, trender och taggar.</p>
          </div>
        </div>
        <button data-testid="run-analysis-button" onClick={runAnalysis} disabled={running}
          className="inline-flex items-center gap-2 bg-[#0033FF] hover:bg-[#002BE6] text-white rounded-full px-5 py-2.5 font-semibold text-sm disabled:opacity-50">
          {running ? <CircleNotch size={16} className="animate-spin" /> : <Sparkle size={16} weight="fill" />}
          {running ? "Analyserar…" : "Kör AI-analys"}
        </button>
      </div>

      {empty ? (
        <div className="mt-10 bg-white border border-zinc-200 rounded-xl py-16 text-center" data-testid="topics-empty">
          <div className="mx-auto h-12 w-12 rounded-xl bg-[#0033FF]/10 flex items-center justify-center"><Tag size={24} weight="duotone" className="text-[#0033FF]" /></div>
          <h3 className="mt-4 font-display font-bold text-lg">Inga ämnen än</h3>
          <p className="mt-1 text-sm text-zinc-500 max-w-sm mx-auto">Klicka på "Kör AI-analys" så taggar och grupperar AI:n dina konversationer efter ämne.</p>
        </div>
      ) : (
        <>
          <div className="mt-8 grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 bg-white border border-zinc-200 rounded-xl p-6">
              <div className="flex items-center justify-between mb-5">
                <h3 className="font-display font-bold text-lg flex items-center gap-2"><ChartBar size={20} weight="bold" className="text-[#0033FF]" /> Topp-ämnen</h3>
                <span className="text-xs font-semibold text-zinc-400">{data.total} analyserade</span>
              </div>
              <ResponsiveContainer width="100%" height={Math.max(220, chartData.length * 36)}>
                <BarChart data={chartData} layout="vertical" margin={{ left: 8, right: 16 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11, fill: "#a1a1aa" }} axisLine={false} tickLine={false} allowDecimals={false} />
                  <YAxis type="category" dataKey="name" width={130} tick={{ fontSize: 12, fill: "#52525b" }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ borderRadius: 10, border: "1px solid #e4e4e7", fontSize: 13 }} cursor={{ fill: "#f4f4f5" }} />
                  <Bar dataKey="value" name="Konversationer" radius={[0, 6, 6, 0]}>
                    {chartData.map((c, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="bg-white border border-zinc-200 rounded-xl p-6">
              <h3 className="font-display font-bold text-lg mb-4 flex items-center gap-2"><Hash size={18} weight="bold" className="text-violet-500" /> Vanligaste taggar</h3>
              <div className="flex flex-wrap gap-2" data-testid="topics-tag-cloud">
                {(data.tags || []).map((t) => {
                  const scale = 0.8 + (t.count / maxTag) * 0.7;
                  return (
                    <span key={t.tag} className="inline-flex items-center gap-1 rounded-full bg-[#0033FF]/8 text-[#0033FF] font-semibold px-2.5 py-1"
                      style={{ fontSize: `${scale * 13}px` }}>
                      {t.tag} <span className="text-[#0033FF]/50">{t.count}</span>
                    </span>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="mt-6 bg-white border border-zinc-200 rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-zinc-100 text-xs font-bold uppercase tracking-[0.12em] text-zinc-400">Alla ämnen</div>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-zinc-400 text-xs uppercase tracking-wider border-b border-zinc-100">
                  <th className="py-3 px-4 font-semibold">Ämne</th>
                  <th className="py-3 px-4 font-semibold text-right">Antal</th>
                  <th className="py-3 px-4 font-semibold text-right">Andel</th>
                  <th className="py-3 px-4 font-semibold text-right">Lösningsgrad</th>
                  <th className="py-3 pl-4 px-4 font-semibold text-right">Snittkvalitet</th>
                </tr>
              </thead>
              <tbody>
                {data.topics.map((t, i) => (
                  <tr key={t.topic} className="border-b border-zinc-50 last:border-0" data-testid="topics-row">
                    <td className="py-3 px-4 font-medium flex items-center gap-2">
                      <span className="h-2.5 w-2.5 rounded-full" style={{ background: PALETTE[i % PALETTE.length] }} />{t.topic}
                    </td>
                    <td className="py-3 px-4 text-right">{t.count}</td>
                    <td className="py-3 px-4 text-right text-zinc-500">{t.share}%</td>
                    <td className="py-3 px-4 text-right">{t.resolved_rate != null ? `${t.resolved_rate}%` : "—"}</td>
                    <td className="py-3 pl-4 px-4 text-right font-semibold">{t.avg != null ? t.avg : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {data.examples?.length > 0 && (
            <div className="mt-6 bg-white border border-zinc-200 rounded-xl p-5" data-testid="topics-examples">
              <div className="text-xs font-bold uppercase tracking-[0.12em] text-zinc-400 mb-3 flex items-center gap-1.5"><ChatCircleText size={14} weight="bold" /> Senaste konversationer</div>
              <div className="space-y-2.5">
                {data.examples.slice(0, 8).map((e, i) => (
                  <div key={i} className="rounded-lg border border-zinc-200 px-3.5 py-2.5">
                    <div className="flex items-center gap-2 flex-wrap">
                      {e.topic && <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-[#0033FF]/10 text-[#0033FF]">{e.topic}</span>}
                      {(e.tags || []).slice(0, 3).map((tg, k) => <span key={k} className="text-[11px] text-zinc-400">#{tg}</span>)}
                      <span className="text-[11px] text-zinc-300 ml-auto">{e.agent_name}</span>
                    </div>
                    <p className="text-sm text-zinc-700 mt-1">{e.summary}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
